import * as _ from "lodash-es";
import type { MRT_ColumnDef, MRT_TableOptions } from "mantine-react-table-open";
import {
  MantineReactTable,
  useMantineReactTable
} from "mantine-react-table-open";
import { useCallback, useMemo, useState } from "react";
import { getDefaultMRTOptions } from "../../../../mantine/getDefaultMRTOptions";
import { backendDateOrDateTimeToLuxonDateTimeOrNull } from "../../../../utils/dates/backendDateOrDateTimeToLuxonDateTimeOrNull";
import { DATE_DISPLAY_FORMAT } from "../../../../utils/dates/dates.constants";
import type {
  PPAInvoice,
  PPAInvoiceDrift,
  PPAInvoiceId
} from "../PPAInvoicePage.types";
import { getBillingPeriodColumn } from "./columns/getBillingPeriodColumn";
import { getButtonColumn } from "./columns/getButtonColumn";
import { getBuyerNameColumn } from "./columns/getBuyerNameColumn";
import { getConsumptionMarketLocationNumbersColumn } from "./columns/getConsumptionMarketLocationNumbersColumn";
import { getDriftsColumn } from "./columns/getDriftsColumn";
import { getFilenameColumn } from "./columns/getFilenameColumn";
import { getGenerationMarketLocationNumbersColumn } from "./columns/getGenerationMarketLocationNumbersColumn";
import { getInvoiceCreationDateColumn } from "./columns/getInvoiceCreationDateColumn";
import { getInvoiceNumberColumn } from "./columns/getInvoiceNumberColumn";
import { getInvoiceTypeColumn } from "./columns/getInvoiceTypeColumn";
import { getSellerNameColumn } from "./columns/getSellerNameColumn";

interface PPAInvoiceTableProps {
  invoices: Array<PPAInvoice>;
  invoiceDrifts: Map<string, PPAInvoiceDrift> | undefined;
  onDownloadDraft: (invoiceId: PPAInvoiceId) => Promise<void>;
  onDownloadInvoice: (invoiceId: PPAInvoiceId) => Promise<void>;
  onGenerateInvoice: (invoiceId: PPAInvoiceId) => Promise<void>;
  onSendInvoice: (invoiceId: PPAInvoiceId) => Promise<void>;
  onVoidInvoice: (invoiceId: PPAInvoiceId) => Promise<void>;
}

function PPAInvoiceTable({
  invoices,
  invoiceDrifts,
  onDownloadDraft,
  onDownloadInvoice,
  onGenerateInvoice,
  onSendInvoice,
  onVoidInvoice
}: PPAInvoiceTableProps) {
  const [loadingDrafts, setLoadingDrafts] = useState<
    Record<PPAInvoiceId, boolean>
  >({});

  const [downloadingInvoices, setDownloadingInvoices] = useState<
    Record<PPAInvoiceId, boolean>
  >({});
  const [generatingInvoices, setGeneratingInvoices] = useState<
    Record<PPAInvoiceId, boolean>
  >({});
  const [sendingInvoices, setSendingInvoices] = useState<
    Record<PPAInvoiceId, boolean>
  >({});
  const [voidingInvoices, setVoidingInvoices] = useState<
    Record<PPAInvoiceId, boolean>
  >({});

  const handleDownloadDraft = useCallback(
    async (invoiceId: PPAInvoiceId) => {
      setLoadingDrafts((prev) => ({ ...prev, [invoiceId]: true }));

      return onDownloadDraft(invoiceId).finally(() => {
        setLoadingDrafts((prev) => ({ ...prev, [invoiceId]: false }));
      });
    },
    [setLoadingDrafts, onDownloadDraft]
  );

  const isLoadingDraft = useCallback(
    (invoiceId: PPAInvoiceId) => {
      return loadingDrafts[invoiceId] || false;
    },
    [loadingDrafts]
  );

  const handleDownloadInvoice = useCallback(
    async (invoiceId: PPAInvoiceId) => {
      setDownloadingInvoices((prev) => ({ ...prev, [invoiceId]: true }));

      return onDownloadInvoice(invoiceId).finally(() => {
        setDownloadingInvoices((prev) => ({ ...prev, [invoiceId]: false }));
      });
    },
    [setDownloadingInvoices, onDownloadInvoice]
  );

  const isDownloadingInvoice = useCallback(
    (invoiceId: PPAInvoiceId) => {
      return downloadingInvoices[invoiceId] || false;
    },
    [downloadingInvoices]
  );

  const handleGenerateInvoice = useCallback(
    async (invoiceId: PPAInvoiceId) => {
      setGeneratingInvoices((prev) => ({ ...prev, [invoiceId]: true }));

      return onGenerateInvoice(invoiceId).finally(() => {
        setGeneratingInvoices((prev) => ({ ...prev, [invoiceId]: false }));
      });
    },
    [setGeneratingInvoices, onGenerateInvoice]
  );

  const isGeneratingInvoice = useCallback(
    (invoiceId: PPAInvoiceId) => {
      return generatingInvoices[invoiceId] || false;
    },
    [generatingInvoices]
  );

  const handleSendInvoice = useCallback(
    async (invoiceId: PPAInvoiceId) => {
      setSendingInvoices((prev) => ({ ...prev, [invoiceId]: true }));

      return onSendInvoice(invoiceId).finally(() => {
        setSendingInvoices((prev) => ({ ...prev, [invoiceId]: false }));
      });
    },
    [setSendingInvoices, onSendInvoice]
  );

  const isSendingInvoice = useCallback(
    (invoiceId: PPAInvoiceId) => {
      return sendingInvoices[invoiceId] || false;
    },
    [sendingInvoices]
  );

  const handleVoidInvoice = useCallback(
    async (invoiceId: PPAInvoiceId) => {
      setVoidingInvoices((prev) => ({ ...prev, [invoiceId]: true }));

      return onVoidInvoice(invoiceId).finally(() => {
        setVoidingInvoices((prev) => ({ ...prev, [invoiceId]: false }));
      });
    },
    [setVoidingInvoices, onVoidInvoice]
  );

  const isVoidingInvoice = useCallback(
    (invoiceId: PPAInvoiceId) => {
      return voidingInvoices[invoiceId] || false;
    },
    [voidingInvoices]
  );

  const defaultOptions = getDefaultMRTOptions<PPAInvoice>({
    emptyRowsFallbackText: "Keine Rechnungen vorhanden.",
    initialState: {
      showGlobalFilter: true,
      sorting: [{ id: "billing_period", desc: true }]
    },
    searchTextInput: {
      placeholder: "Suche nach Zeitraum, Dokument, Lieferant, ..."
    }
  });

  const customTableOptions: Omit<
    MRT_TableOptions<PPAInvoice>,
    "columns" | "data"
  > = {
    enableTopToolbar: true,
    columnFilterDisplayMode: "popover",
    enableColumnResizing: true,
    enableFacetedValues: true,
    filterFns: {
      filterRelevantColumns: (row, _, filterValue) => {
        const lowerCaseFilterValue = filterValue.toLowerCase();
        const billingPeriod = row.getValue<string>("billing_period");
        const filename = row.getValue<string>("filename");
        const invoiceNumber = row.getValue<string | null>("invoice_number");
        const invoiceCreationDate = backendDateOrDateTimeToLuxonDateTimeOrNull(
          row.getValue<string | null>("invoice_creation_date")
        )?.toFormat(DATE_DISPLAY_FORMAT);
        const sellerName = row.getValue<string>("seller_name");
        const buyerName = row.getValue<string>("buyer_name");
        const generationMarketLocationNumbers = row.getValue<Array<string>>(
          "generation_market_location_numbers"
        );
        const consumptionMarketLocationNumbers = row.getValue<Array<string>>(
          "consumption_market_location_numbers"
        );

        return (
          billingPeriod.toLowerCase().includes(lowerCaseFilterValue) ||
          filename.toLowerCase().includes(lowerCaseFilterValue) ||
          invoiceNumber?.toLowerCase().includes(lowerCaseFilterValue) ||
          invoiceCreationDate?.toLowerCase().includes(lowerCaseFilterValue) ||
          sellerName.toLowerCase().includes(lowerCaseFilterValue) ||
          buyerName.toLowerCase().includes(lowerCaseFilterValue) ||
          generationMarketLocationNumbers.some((site) =>
            site.toLowerCase().includes(lowerCaseFilterValue)
          ) ||
          consumptionMarketLocationNumbers.some((site) =>
            site.toLowerCase().includes(lowerCaseFilterValue)
          )
        );
      }
    },
    globalFilterFn: "filterRelevantColumns"
  };

  const tableOptions: Omit<
    MRT_TableOptions<PPAInvoice>,
    "columns" | "data"
  > = _.merge({}, defaultOptions, customTableOptions);

  const columnsMRT: Array<MRT_ColumnDef<PPAInvoice>> = useMemo(() => {
    return [
      getBillingPeriodColumn(),
      getDriftsColumn(invoiceDrifts, handleDownloadDraft, isLoadingDraft),
      getFilenameColumn(isDownloadingInvoice, handleDownloadInvoice),
      getInvoiceNumberColumn(),
      getInvoiceTypeColumn(isLoadingDraft, handleDownloadDraft),
      getInvoiceCreationDateColumn(),
      getGenerationMarketLocationNumbersColumn(invoices),
      getConsumptionMarketLocationNumbersColumn(invoices),
      getSellerNameColumn(),
      getBuyerNameColumn(),
      getButtonColumn(
        isGeneratingInvoice,
        isSendingInvoice,
        isVoidingInvoice,
        handleGenerateInvoice,
        handleSendInvoice,
        handleVoidInvoice
      )
    ];
  }, [
    invoices,
    invoiceDrifts,
    handleDownloadDraft,
    isLoadingDraft,
    isDownloadingInvoice,
    handleDownloadInvoice,
    isGeneratingInvoice,
    handleGenerateInvoice,
    isSendingInvoice,
    handleSendInvoice,
    isVoidingInvoice,
    handleVoidInvoice
  ]);

  const table = useMantineReactTable({
    ...tableOptions,
    columns: columnsMRT,
    data: invoices
  });

  return <MantineReactTable table={table} />;
}

export { PPAInvoiceTable, PPAInvoiceTableProps };
